import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

export const VideoShowcase = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section
      id="showcase"
      data-testid="video-showcase-section"
      className="relative py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl"
      >
        <p className="text-xs uppercase tracking-[0.2em] text-[#22d3ee] font-bold mb-4">
          See us in motion
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
          Where ideas are forged into growth.
        </h2>
        <p className="mt-6 text-slate-400 leading-relaxed">
          A glimpse into how NetForge blends engineering, design and marketing to
          build the systems that scale modern businesses.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="mt-12 relative group"
      >
        <div className="absolute -inset-px bg-gradient-to-tr from-[#22d3ee]/40 via-transparent to-[#22d3ee]/20 rounded-sm blur-[2px]" />
        <div className="relative rounded-sm overflow-hidden border border-white/10 bg-black">
          <video
            ref={videoRef}
            data-testid="showcase-video"
            className="w-full h-auto block aspect-video object-cover"
            poster="/promo-poster.jpg"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/promo.mp4" type="video/mp4" />
          </video>

          {/* Controls */}
          <div className="absolute bottom-4 right-4 flex items-center gap-3">
            <button
              data-testid="showcase-play-toggle"
              onClick={togglePlay}
              aria-label={playing ? "Pause video" : "Play video"}
              className="w-11 h-11 flex items-center justify-center bg-[#0a0f1e]/70 backdrop-blur-md border border-white/15 rounded-full text-white hover:bg-[#22d3ee] hover:text-[#0a0f1e] transition-colors"
            >
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <button
              data-testid="showcase-mute-toggle"
              onClick={toggleMute}
              aria-label={muted ? "Unmute video" : "Mute video"}
              className="w-11 h-11 flex items-center justify-center bg-[#0a0f1e]/70 backdrop-blur-md border border-white/15 rounded-full text-white hover:bg-[#22d3ee] hover:text-[#0a0f1e] transition-colors"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
